import { Flex } from '@chakra-ui/react'
import type { MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import { Tooltip } from '~/components/ui/tooltip'
import { ApplicationRoutes } from '~/hooks/useRouting'
import { HealthIndicatorIcon } from '~/pages/Status/HealthIndicatorIcon'
import { setStatusReturnTo } from '~/pages/Status/StatusPanel'
import { useOverallHealth } from '~/pages/Status/useOverallHealth'

/**
 * Compact top-nav indicator that surfaces an app problem (connection or
 * desktop updates) and routes to the status panel. Renders nothing for
 * healthy/idle/unknown — only a genuine problem (degraded/down) shows the
 * icon, so users who don't run the webphone never see a false alarm. The full
 * label lives in a hover tooltip to keep the nav uncluttered.
 */
export const ConnectionStatusChip = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const overall = useOverallHealth()

  if (overall !== 'degraded' && overall !== 'down') return null

  const label =
    overall === 'down'
      ? t('statusPanel.navChipDown')
      : t('statusPanel.navChipDegraded')

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    // Use the router's location, not window.location, so a pending navigation
    // can't capture a stale URL as the return-to target.
    setStatusReturnTo(location.pathname + location.search, event.currentTarget)
    navigate(ApplicationRoutes.STATUS)
  }

  return (
    <Tooltip content={label} showArrow>
      <Flex
        as="button"
        // Native button defaults to type="submit"; force "button" so it can
        // never submit a form if this chip is ever nested in one. Flex's prop
        // types don't include the native button attrs, hence the cast.
        {...({ type: 'button' } as { type: 'button' })}
        align="center"
        justify="center"
        boxSize={8}
        borderRadius="md"
        cursor="pointer"
        _hover={{ bg: 'bg.muted' }}
        onClick={handleClick}
        aria-label={t('statusPanel.navChipAria')}
      >
        <HealthIndicatorIcon level={overall} />
      </Flex>
    </Tooltip>
  )
}
